/* Global system-notifications bell.
 *
 * Distinct from the forum NotificationsBell (which only surfaces forum
 * activity). This bell handles system events written to db.notifications by:
 *   - A2P 10DLC webhook (campaign verified/rejected, brand approved/rejected)
 *   - Future: seat-billing overage receipts, deal-closed events, etc.
 *
 * Polls /notifications/inbox/unread-count every 30s for the badge.
 * Click → opens a dropdown showing the 10 latest notifications.
 * Click a row → mark-read + navigate to its `link`.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import {
  BellDot,
  Bell,
  Check,
  CheckCheck,
  CheckCircle2,
  AlertTriangle,
  ShieldCheck,
  Info,
} from "lucide-react";

const SEVERITY_META = {
  success: { Icon: CheckCircle2, color: "text-emerald-600" },
  error:   { Icon: AlertTriangle, color: "text-rose-600" },
  info:    { Icon: Info, color: "text-sky-600" },
  warning: { Icon: AlertTriangle, color: "text-amber-600" },
};

const KIND_OVERRIDE = {
  a2p_campaign_verified: { Icon: ShieldCheck, color: "text-emerald-600" },
  a2p_brand_approved:    { Icon: ShieldCheck, color: "text-emerald-600" },
};

function timeAgo(iso) {
  if (!iso) return "";
  const seconds = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (seconds < 60) return `${seconds}s ago`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
  return `${Math.floor(seconds / 86400)}d ago`;
}

export default function SystemNotificationsBell() {
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [count, setCount] = useState(0);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const ref = useRef(null);

  const loadCount = useCallback(async () => {
    try {
      const { data } = await api.get("/notifications/inbox/unread-count");
      setCount(data?.count || 0);
    } catch { /* swallow — bell stays at last known count */ }
  }, []);

  const loadItems = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/notifications/inbox", { params: { limit: 10 } });
      setItems(data?.items || []);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // Initial fetch + 30s poll.
  useEffect(() => {
    loadCount();
    const id = setInterval(loadCount, 30000);
    return () => clearInterval(id);
  }, [loadCount]);

  // Outside-click closes the dropdown.
  useEffect(() => {
    if (!open) return;
    const onClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  const toggle = async () => {
    const next = !open;
    setOpen(next);
    if (next) await loadItems();
  };

  const onRowClick = async (item) => {
    setOpen(false);
    // Mark read locally + on the server.
    if (!item.read_at) {
      setCount((c) => Math.max(0, c - 1));
      setItems((arr) => arr.map((it) => (
        it.id === item.id ? { ...it, read_at: new Date().toISOString() } : it
      )));
      try {
        await api.post(`/notifications/inbox/${item.id}/read`);
      } catch { /* swallow — local optimism is fine */ }
    }
    if (item.link) nav(item.link);
  };

  const markAllRead = async () => {
    setCount(0);
    setItems((arr) => arr.map((it) => ({ ...it, read_at: it.read_at || new Date().toISOString() })));
    try {
      await api.post("/notifications/inbox/read-all");
    } catch { /* swallow */ }
  };

  const badge = count > 99 ? "99+" : count;

  return (
    <div ref={ref} className="fixed top-3 right-16 z-40" data-testid="system-bell">
      <button
        type="button"
        aria-label={`Notifications${count ? ` (${count} unread)` : ""}`}
        onClick={toggle}
        className="relative w-9 h-9 rounded-full bg-white border border-zinc-200 shadow-sm hover:border-zinc-400 flex items-center justify-center text-zinc-700"
        title="System notifications"
        data-testid="system-bell-toggle"
      >
        {count > 0 ? <BellDot size={16} /> : <Bell size={16} />}
        {count > 0 && (
          <span
            className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-rose-600 text-white text-[10px] font-bold flex items-center justify-center"
            data-testid="system-bell-badge"
          >
            {badge}
          </span>
        )}
      </button>

      {open && (
        <div
          className="absolute top-11 right-0 w-80 max-h-[28rem] overflow-y-auto bg-white border border-zinc-200 rounded-lg shadow-xl"
          data-testid="system-bell-dropdown"
        >
          <div className="px-3 py-2.5 border-b border-[var(--line)] flex items-center justify-between sticky top-0 bg-white">
            <span className="text-xs font-bold uppercase tracking-wide">Notifications</span>
            {count > 0 && (
              <button
                type="button"
                onClick={markAllRead}
                className="text-[11px] text-emerald-700 hover:text-emerald-800 inline-flex items-center gap-1 font-semibold"
                data-testid="system-bell-mark-all"
              >
                <CheckCheck size={11} /> Mark all read
              </button>
            )}
          </div>

          {loading ? (
            <div className="px-3 py-6 text-center text-xs text-[var(--ink-2)]">Loading…</div>
          ) : items.length === 0 ? (
            <div className="px-3 py-8 text-center">
              <Check size={22} className="mx-auto mb-2 text-emerald-600" />
              <div className="text-sm font-semibold">You&apos;re all caught up</div>
              <div className="text-xs text-[var(--ink-2)] mt-1">
                System notifications (A2P approvals, billing events, etc.) will land here.
              </div>
            </div>
          ) : (
            <ul className="divide-y divide-[var(--line)]">
              {items.map((it) => {
                const meta = KIND_OVERRIDE[it.kind] || SEVERITY_META[it.severity] || SEVERITY_META.info;
                const Icon = meta.Icon;
                return (
                  <li key={it.id}>
                    <button
                      type="button"
                      onClick={() => onRowClick(it)}
                      className={`w-full text-left px-3 py-2.5 hover:bg-[var(--surface-2)] flex items-start gap-2.5 ${
                        !it.read_at ? "bg-emerald-50/40" : ""
                      }`}
                      data-testid={`system-bell-row-${it.id}`}
                    >
                      <Icon size={16} className={`shrink-0 mt-0.5 ${meta.color}`} />
                      <div className="flex-1 min-w-0">
                        <div className={`text-sm leading-snug ${!it.read_at ? "font-semibold" : ""}`}>
                          {it.title}
                        </div>
                        {it.body && (
                          <div className="text-xs text-[var(--ink-2)] mt-0.5 line-clamp-2">{it.body}</div>
                        )}
                        <div className="text-[10px] text-[var(--ink-2)] mt-1">{timeAgo(it.created_at)}</div>
                      </div>
                      {!it.read_at && (
                        <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 mt-1.5 shrink-0" />
                      )}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
